import type { Attachment, Communication } from '@medplum/fhirtypes'
import { Button, Divider, Loader, Modal, Stack, Text, Title } from '@mantine/core'
import { useState, useEffect } from 'react'
import { createReference, formatDateTime } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { IconFile } from '@tabler/icons-react'
import { NoteForm } from './NoteForm'
import { extractNextAction, extractNote, extractAttachments } from './utils'
import { downloadAttachment } from '@/utils/downloadAttachment.ts'
import { PdfViewer } from '@/components/PdfViewer'

type Props = {
  note: Communication
  onUpdate: (
    noteId: string,
    note: string,
    nextAction: string,
    newFiles: File[],
    keptAttachments: Attachment[],
  ) => Promise<void>
}

function isPdfAttachment(attachment: Attachment): boolean {
  return (
    attachment.contentType?.toLowerCase().includes('pdf') ||
    attachment.title?.toLowerCase().endsWith('.pdf') ||
    false
  )
}

function DownloadedAttachmentPreview({ attachment }: { attachment: Attachment }) {
  const medplum = useMedplum()
  const [objectUrl, setObjectUrl] = useState<string>()
  const [error, setError] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    if (!attachment.url) return

    setObjectUrl(undefined)
    setError(false)

    let cancelled = false
    let createdUrl = ''

    downloadAttachment(medplum, attachment.url)
      .then((blob) => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setObjectUrl(createdUrl)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [attachment.url, medplum])

  if (error) {
    return (
      <Text size="xs" c="red">
        Failed to load attachment
      </Text>
    )
  }

  if (!objectUrl)
    return attachment.url ? (
      <Loader size="xs" />
    ) : (
      <Text size="xs" c="red">
        Failed to load attachment
      </Text>
    )

  if (attachment.contentType?.startsWith('image/')) {
    return (
      <>
        <img
          src={objectUrl}
          alt={attachment.title ?? 'Attachment'}
          style={{
            maxHeight: 200,
            maxWidth: '100%',
            borderRadius: 4,
            display: 'block',
            cursor: 'zoom-in',
          }}
          onClick={() => setLightboxOpen(true)}
        />
        <Modal
          opened={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          title={attachment.title ?? 'Attachment'}
          size="xl"
          centered
        >
          <img
            src={objectUrl}
            alt={attachment.title ?? 'Attachment'}
            style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }}
          />
        </Modal>
      </>
    )
  }

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <iframe
        src={objectUrl}
        title={attachment.title ?? 'PDF'}
        style={{ width: '100%', height: '60vh', border: 'none', display: 'block' }}
      />
    </div>
  )
}

function AttachmentPreview({ attachment }: { attachment: Attachment }) {
  if (attachment.url && isPdfAttachment(attachment)) {
    return <PdfViewer fileUrl={attachment.url} className="h-[60vh]" />
  }

  return <DownloadedAttachmentPreview attachment={attachment} />
}

export function NoteDetail({ note, onUpdate }: Props) {
  const medplum = useMedplum()
  const [editOpen, setEditOpen] = useState(false)

  const noteText = extractNote(note)
  const nextActionText = extractNextAction(note)
  const attachments = extractAttachments(note)
  const author = note.sender?.display || 'Unknown'
  const date = formatDateTime(note.meta?.lastUpdated || note.sent || '')

  const profile = medplum.getProfile()
  const isCreator = profile ? note.sender?.reference === createReference(profile).reference : false

  const handleUpdate = async (
    updatedNote: string,
    updatedNextAction: string,
    newFiles: File[],
    keptAttachments: Attachment[],
  ) => {
    await onUpdate(note.id!, updatedNote, updatedNextAction, newFiles, keptAttachments)
    setEditOpen(false)
  }

  return (
    <>
      <Stack p="xl" gap="lg" className="h-full">
        <div className="flex items-start justify-between">
          <div>
            <Text size="sm" c="dimmed">
              {author} &middot; {date}
            </Text>
          </div>
          {isCreator && (
            <Button variant="outline" size="xs" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
        </div>

        <Divider />

        <div>
          <Title order={6} mb="xs" c="dimmed">
            Note
          </Title>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{noteText || '—'}</Text>
        </div>

        {nextActionText && (
          <>
            <Divider />
            <div>
              <Title order={6} mb="xs" c="dimmed">
                Next Action
              </Title>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{nextActionText}</Text>
            </div>
          </>
        )}

        {attachments.length > 0 && (
          <>
            <Divider />
            <div>
              <Title order={6} mb="xs" c="dimmed">
                Attachments
              </Title>
              <Stack gap="sm">
                {attachments.map((a) => (
                  <div key={a.url ?? a.title}>
                    <div className="mb-1 flex items-center gap-1">
                      <IconFile size={13} className="text-gray-400" />
                      <Text size="xs" c="dimmed">
                        {a.title ?? 'Attachment'}
                      </Text>
                    </div>
                    <AttachmentPreview attachment={a} />
                  </div>
                ))}
              </Stack>
            </div>
          </>
        )}
      </Stack>

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title="Edit Note" size="lg">
        <NoteForm
          initialNote={noteText}
          initialNextAction={nextActionText}
          initialAttachments={attachments}
          onSubmit={handleUpdate}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </>
  )
}
